import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiDelete, apiDownload, ApiError } from '../api/client';
import { businessBySlug, BUSINESSES } from '../domain/businesses';
import { formatDate, todayIso } from '../lib/format';
import { btn } from '../components/FilterBar';
import MemberModal from '../components/MemberModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/**
 * Klientat — gym memberships. Ports frmKlientat and frmKlientat{Stargym,Missfit}.
 *
 * Read and delete only. The ERP never created or edited memberships — checked
 * across all five desktop forms, there is no INSERT or UPDATE against
 * gym_memberships anywhere. Members are registered by the separate signup
 * application that owns those databases.
 */

interface MemberRow {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  gender: string;
  phone_number: string;
  email: string;
  package_type: string;
  created_at: string | null;
  end_at: string | null;
  hasPhoto: number;
}

/** Registrations go back years, so the default range is wide. */
function yearsAgoIso(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0, 10);
}

export default function Klientat() {
  const { biznesi = 'stargym' } = useParams();
  const business = businessBySlug(biznesi);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(() => yearsAgoIso(5));
  const [endDate, setEndDate] = useState(todayIso);
  const [status, setStatus] = useState<'all' | 'active' | 'expired'>('all');
  const [viewing, setViewing] = useState<MemberRow | null>(null);
  const [deleting, setDeleting] = useState<MemberRow | null>(null);

  useEffect(() => setSearch(''), [biznesi]);

  const hasMemberships = business?.hasMemberships ?? false;

  const totalsQuery = useQuery({
    queryKey: ['klientat-totalet'],
    queryFn: () =>
      apiGet<{ businesses: { slug: string; name: string; newThisMonth: number }[] }>(
        '/klientat/totalet',
      ),
  });

  const listQuery = useQuery({
    queryKey: ['klientat', biznesi, search, startDate, endDate, status],
    queryFn: () =>
      apiGet<{ rows: MemberRow[]; total: number; active: number; expired: number }>(
        `/klientat/${biznesi}`,
        { search, startDate, endDate, status },
      ),
    enabled: hasMemberships,
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/klientat/${biznesi}/klienti/${id}`),
    onSuccess: () => {
      toast.success('Klienti u fshi me sukses.');
      void queryClient.invalidateQueries({ queryKey: ['klientat'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  if (!business) {
    return <div className="rounded-xl bg-white p-6 text-red-700">Biznesi nuk u gjet.</div>;
  }

  if (!hasMemberships) {
    return (
      <div className="rounded-xl bg-white p-8 text-center">
        <h1 className="text-lg font-semibold">Klientat — {business.name}</h1>
        <p className="mt-2 text-sm text-muted">
          {business.name} nuk ka bazë klientësh. Vetëm palestrat kanë antarësime.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          {BUSINESSES.filter((b) => b.hasMemberships).map((b) => (
            <Link key={b.slug} to={`/klientat/${b.slug}`} className={btn.primary}>
              {b.name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Klientat — {business.name}</h1>
          <p className="text-sm text-muted">
            {listQuery.data?.total ?? 0} gjithsej ·{' '}
            <span className="text-emerald-700">{listQuery.data?.active ?? 0} aktivë</span> ·{' '}
            <span className="text-muted">{listQuery.data?.expired ?? 0} të skaduar</span>
          </p>
          {/*
            Anëtarësimet e reja këtë muaj, për të dyja palestrat — the two
            counters frmKlientat shows above this list (frmKlientat.cs:47).
          */}
          {totalsQuery.data && (
            <p className="mt-1 text-sm text-muted">
              Të reja këtë muaj:{' '}
              {totalsQuery.data.businesses.map((b, i) => (
                <span key={b.slug}>
                  {i > 0 ? ' · ' : ''}
                  {b.name}{' '}
                  <span className="font-semibold text-slate-700">{b.newThisMonth}</span>
                </span>
              ))}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {BUSINESSES.filter((b) => b.hasMemberships).map((b) => (
            <Link
              key={b.slug}
              to={`/klientat/${b.slug}`}
              className={b.slug === biznesi ? btn.primary : btn.secondary}
            >
              {b.name}
            </Link>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-52 flex-1">
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="kl-search">
              Kërko emër, email ose telefon
            </label>
            <input
              id="kl-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Shkruani për të filtruar…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="kl-status">
              Statusi
            </label>
            <select
              id="kl-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            >
              <option value="all">Të gjithë</option>
              <option value="active">Aktivë</option>
              <option value="expired">Të skaduar</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="kl-start">
              Regjistruar prej
            </label>
            <input
              id="kl-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="kl-end">
              Deri
            </label>
            <input
              id="kl-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
            />
          </div>

          <button
            type="button"
            onClick={() =>
              apiDownload(`/klientat/${biznesi}/eksport`, {
                search,
                startDate,
                endDate,
                status,
              }).catch((err) =>
                toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.'),
              )
            }
            disabled={rows.length === 0}
            className={`ml-auto ${btn.secondary}`}
          >
            Eksporto Excel
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Klienti</th>
                <th className="px-4 py-3 font-semibold">Gjinia</th>
                <th className="px-4 py-3 font-semibold">Kontakti</th>
                <th className="px-4 py-3 font-semibold">Paketa</th>
                <th className="px-4 py-3 font-semibold">Regjistruar</th>
                <th className="px-4 py-3 font-semibold">Vlen deri</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!listQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted">
                    Nuk ka klientë për këto filtra.
                  </td>
                </tr>
              )}

              {rows.map((row) => {
                const active = row.end_at ? row.end_at.slice(0, 10) >= todayIso() : false;
                return (
                  <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        onClick={() => setViewing(row)}
                        className="font-medium text-brand-600 hover:underline"
                      >
                        {row.first_name} {row.last_name}
                      </button>
                      <span className="block text-xs text-muted">
                        {formatDate(row.birthday)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted">{row.gender}</td>
                    <td className="px-4 py-2.5">
                      {row.email && (
                        <a href={`mailto:${row.email}`} className="block text-brand-600 hover:underline">
                          {row.email}
                        </a>
                      )}
                      {row.phone_number && (
                        <span className="block text-muted">{row.phone_number}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">{row.package_type} muaj</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.created_at)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {formatDate(row.end_at)}
                      <span
                        className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                          active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {active ? 'Aktiv' : 'Skaduar'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setViewing(row)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                        >
                          Detajet
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleting(row)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          Fshij
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {viewing && (
        <MemberModal biznesi={biznesi} memberId={viewing.id} onClose={() => setViewing(null)} />
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message={`${deleting.first_name} ${deleting.last_name} do të fshihet përgjithmonë nga baza e antarësimeve. Ky veprim nuk kthehet.`}
          confirmLabel="Fshij përgjithmonë"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.id);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
